import "./styles.css";
import { createRoot } from "react-dom/client";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import {
  forwardRef,
  Suspense,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Physics,
  Quad,
  Triplet,
  useCylinder,
  usePlane,
} from "@react-three/cannon";
import gsap from "gsap";
import { Euler, PCFSoftShadowMap, Quaternion, Vector3 } from "three";
import { PositionalAudio, useProgress, useTexture } from "@react-three/drei";
import { DepthOfField, EffectComposer } from "@react-three/postprocessing";
import * as THREE from "three";

const randomInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min) + min);

function DOF() {
  const dof = useRef<any>(null);
  const { camera } = useThree();

  const focusDistance = useMemo(() => {
    const focusUnits = 65;
    return (focusUnits - camera.near) / (camera.far - camera.near);
  }, [camera.near, camera.far]);

  return (
    <EffectComposer multisampling={0}>
      <DepthOfField
        ref={dof}
        focusDistance={focusDistance}
        focalLength={0.9} // strength of blur
        bokehScale={10} // highlight bokeh size
        height={480}
      />
    </EffectComposer>
  );
}

function App() {
  const [resultsList, setResultsList] = useState<boolean[]>([]);
  const [coinCanFlip, setCoinCanFlip] = useState(true);
  const coinRef = useRef<CoinHandle>(null);

  function addResult(res: boolean) {
    setResultsList((a) => [...a, res]);
  }

  return (
    <main>
      <div id="canvas-container">
        <Suspense
          fallback={
            <div className="loader-container">
              <Loader />
            </div>
          }
        >
          <Canvas
            shadows={{ type: PCFSoftShadowMap }}
            camera={{ position: [0, 70, 0], fov: 5, near: 0.01, far: 100 }}
          >
            <Physics allowSleep={false} iterations={15} gravity={[0, -200, 0]}>
              <Coin
                position={[0, 0, 0]}
                onResult={addResult}
                ref={coinRef}
                setCanFlip={setCoinCanFlip}
              />
              <Floor position={[0, -1, 0]} rotation={[-Math.PI / 2, 0, 0]} />
              <Floor position={[0, 50, 0]} rotation={[Math.PI / 2, 0, 0]} />
            </Physics>
            <ambientLight intensity={0.1} />
            <directionalLight
              color="white"
              position={[-3, 75, -3]}
              castShadow
            />
          </Canvas>
        </Suspense>
      </div>
      <div className="buttons">
        <button
          className={"flip-button" + (coinCanFlip ? "" : " deactivated")}
          onClick={() => coinRef.current?.flip()}
        >
          FLIP
        </button>
      </div>
      <hr />
      <ResultList results={resultsList} />
    </main>
  );
}

function Loader() {
  const { active, progress } = useProgress();

  if (!active) return null;
  return (
    <div className="loader">
      <div>Loading...</div>
      <div>{progress}%</div>
    </div>
  );
}

type CoinHandle = {
  flip: () => void;
  reset: () => void;
};

type CoinProps = {
  position: Triplet;
  onResult: (res: boolean) => void;
  setCanFlip: (res: boolean) => void;
};

const Coin = forwardRef<CoinHandle, CoinProps>(function Coin(
  { position, onResult, setCanFlip },
  ref
) {
  const thickness = 0.1;
  const minSpin = 200;
  const maxSpin = 400;
  const minHeight = 100;
  const maxHeight = 250;
  const last = useRef(0);
  const [cyl, api] = useCylinder(() => ({
    type: "Dynamic",
    mass: 0,
    args: [1, 1, thickness, 36],
    linearDamping: 0.95,
    angularDamping: 0.2,
    position,
    onCollide: (e) => {
      const now = performance.now();
      if (now - last.current < 60) return;
      last.current = now;

      // collision "strength" approximation
      const impact = e.contact.impactVelocity;

      // gate + scale volume (tune to taste)
      const v = Math.min(0.2, Math.max(0.1, impact ?? 0));
      const sound = flipSound.current;
      if (sound) {
        sound.setVolume?.(v);
        sound.stop();
        sound.play();
      }
    },
  }));

  const [topTex, bottomTex] = useTexture([
    "textures/ct_logo.png",
    "textures/t_logo.png",
  ]);

  useEffect(() => {
    topTex.center.set(0.5, 0.5);
    topTex.rotation = Math.PI / 2;
    bottomTex.center.set(0.5, 0.5);
    bottomTex.rotation = Math.PI / 2;
  }, [topTex, bottomTex]);

  useImperativeHandle(ref, () => ({
    flip,
    reset,
  }));

  const flipSound = useRef<THREE.PositionalAudio>(null);
  const landSound = useRef<THREE.PositionalAudio>(null);
  const rollSound = useRef<THREE.PositionalAudio>(null);

  const isFlipping = useRef(false);
  const pastResetThreshold = useRef(false);
  const isAnimating = useRef(false);
  const pos = useRef<Triplet>(position);
  const rot = useRef<Triplet>([0, 0, 0]);
  const angularVelocity = useRef<Triplet>([0, 0, 0]);
  const quat = useRef<Quad>([0, 0, 0, 1]);

  api.position.subscribe((p) => (pos.current = p));
  api.rotation.subscribe((r) => (rot.current = r));
  api.angularVelocity.subscribe((a) => (angularVelocity.current = a));
  api.quaternion.subscribe((q) => (quat.current = q));

  function isLayingFlat(threshold: number) {
    const euler = new Euler(rot.current[0], rot.current[1], rot.current[2]);
    const objUp = new Vector3(0, 1, 0).applyEuler(euler).normalize();
    const worldUp = new Vector3(0, 1, 0);

    const cross = new Vector3().crossVectors(objUp, worldUp);
    return cross.lengthSq() < threshold;
  }

  function isHeadsUp() {
    const euler = new Euler(rot.current[0], rot.current[1], rot.current[2]);
    const objUp = new Vector3(0, 1, 0).applyEuler(euler).normalize();
    return objUp.y >= 0;
  }

  function isBelowHeight(height: number) {
    return pos.current[1] <= height;
  }

  function isAboveHeight(height: number) {
    return pos.current[1] > height;
  }

  function isFlippingTooSlowly(threshold: number) {
    const angular =
      angularVelocity.current[0] ** 2 + angularVelocity.current[2] ** 2;
    return angular <= threshold;
  }

  function enablePhyiscs() {
    api.mass.set(1);
  }

  function disablePhysics() {
    api.velocity.set(0, 0, 0);
    api.angularVelocity.set(0, 0, 0);
    api.mass.set(0);
  }

  useFrame(() => {
    if (isFlipping.current && isFlippingTooSlowly(20) && isAboveHeight(0)) {
      torqueFlip();
    }

    if (isAboveHeight(0)) pastResetThreshold.current = true;
    if (pastResetThreshold.current && isBelowHeight(0) && isLayingFlat(0.1)) {
      isFlipping.current = false;
      pastResetThreshold.current = false;
      isAnimating.current = true;

      playLandSound();
      disablePhysics();

      // Animated values
      const state = {
        x: pos.current[0],
        y: pos.current[1],
        z: pos.current[2],
        yRot: rot.current[1],
      };

      const endState = {
        x: 0,
        y: 0,
        z: 0,
        yRot: 0,
      };

      gsap.to(state, {
        ...endState,
        delay: 0.3,
        duration: 0.3,
        ease: "power2.out",
        onUpdate: () => {
          api.position.set(state.x, state.y, state.z);
          api.rotation.set(rot.current[0], state.yRot, rot.current[2]);
        },
        onComplete: () => onLand(),
      });
    }
  });

  const flip = useCallback(() => {
    if (isFlipping.current || isAnimating.current) return;
    enablePhyiscs();

    setCanFlip(false);
    isFlipping.current = true;
    playFlipSound();
    api.velocity.set(0, randomInt(minHeight, maxHeight), 0);
    torqueFlip();
  }, [api]);

  function torqueFlip() {
    let spin = randomInt(minSpin, maxSpin) * (Math.random() < 0.5 ? -1 : 1);
    let altSpin1 =
      randomInt(minSpin / 10, maxSpin / 10) * (Math.random() < 0.5 ? -1 : 1);
    let altSpin2 =
      randomInt(minSpin / 10, maxSpin / 10) * (Math.random() < 0.5 ? -1 : 1);
    altSpin1 *= Math.random() < 0.2 ? 1 : 0;
    altSpin2 *= Math.random() < 0.2 ? 1 : 0;
    api.applyTorque([altSpin1, altSpin2, spin]);
  }

  function onLand() {
    isAnimating.current = false;
    onResult(isHeadsUp());
    setCanFlip(true);
  }

  const reset = useCallback(() => {
    api.position.set(position[0], position[1], position[2]);
    api.rotation.set(0, 0, 0);
    api.velocity.set(0, 0, 0);
    api.angularVelocity.set(0, 0, 0);
  }, [api]);

  const playFlipSound = () => {
    const audio = flipSound.current;
    if (!audio) return;
    audio.setVolume(1);
    audio.detune = randomInt(-150, 150);
    audio.stop();
    audio.play();
  };

  const playLandSound = () => {
    const audio = landSound.current;
    if (!audio) return;
    audio.setVolume(0.02);
    audio.detune = randomInt(0, 200);
    audio.stop();
    audio.play();
  };

  return (
    <group ref={cyl}>
      <mesh receiveShadow onPointerDown={flip} castShadow>
        <cylinderGeometry args={[1, 1, thickness, 36]} />
        <meshStandardMaterial attach="material-0" color={"#555"} />
        <meshStandardMaterial
          attach="material-1"
          color={"#ffffff"}
          map={topTex}
        />
        <meshStandardMaterial
          attach="material-2"
          color={"#ffffff"}
          map={bottomTex}
        />
        <PositionalAudio
          ref={flipSound}
          url="sounds/coin_flip.wav"
          distance={100}
          autoplay={false}
          loop={false}
        />
        <PositionalAudio
          ref={landSound}
          url="sounds/coin_land.wav"
          distance={100}
          autoplay={false}
          loop={false}
          duration={0.5}
          offset={0.3}
        />
        {/* <PositionalAudio
          ref={rollSound}
          url="sounds/coin_roll.flac"
          distance={100}
          autoplay={false}
          loop={false}
          duration={0.2}
        /> */}
      </mesh>
    </group>
  );
});

function Floor(props: any) {
  const [ref, api] = usePlane(() => ({ type: "Static", ...props }));
  return (
    <mesh ref={ref} receiveShadow>
      <planeGeometry args={[100, 100]} />
      <shadowMaterial opacity={0.4} />
    </mesh>
  );
}

function ResultList({ results }: { results: boolean[] }) {
  return (
    <div className="result-list">
      {results.map((r, i) => (
        <ResultListItem value={r} key={i} />
      ))}
    </div>
  );
}

function ResultListItem({ value }: { value: boolean }) {
  const resText = value ? "heads" : "tails";
  return (
    <div className={"result-list-item " + resText}>
      <img
        src={value ? "textures/ct_logo.png" : "textures/t_logo.png"}
        alt="CT Logo"
      />
      {value ? "Heads" : "Tails"}
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
